<template>
  <base-container>
    <header-back-button :title="group?.name ?? $route.query.name as string ?? 'Détails du groupe'" />
    <div
      v-if="!loading"
      class="flex flex-1 flex-col p-6 gap-6"
    >
      <template v-if="group">
        <div class="flex gap-3">
          <p
            v-if="!editMode"
            class="text-center text-primary flex-1"
          >
            {{ group.description }}
          </p>
          <div
            v-if="editMode"
            class="flex-1"
          />
          <base-button
            v-if="isOwner"
            class="flex items-center gap-2 bg-white self-start"
            @click="editMode = !editMode"
          >
            <v-icon
              :size="20"
              color="black"
            >
              mdi-pencil
            </v-icon>
            <span>
              Modifier le groupe
            </span>
          </base-button>
        </div>
        <healthcenter-group-edit-form
          v-if="editMode"
          :group="group"
          @saved="handleGroupUpdated"
        />
        <invite-healthcenter-card
          v-if="isOwner"
          :healthcenter-group-id="group.id"
          @invitation-sent="handleInvitationSent"
        />
        <healthcenter-group-sent-invitation-card
          v-if="!!pendingInvitations?.length && isOwner"
          :invitations="pendingInvitations"
          @cancel-invitation="deleteInvitation"
        />
        <HealthcenterGroupingGroupCard
          :group-id="group.id"
          :healthcenters="group.healthcenters"
          @update-sharing="refresh"
        />
        <healthcenter-group-healthcenters
          :owner-access-id="group.creator_access_id"
          :healthcenters="group.healthcenters"
          :group-id="group.id"
          @member-deleted="handleMemberDeleted"
        />
        <base-button
          v-if="isOwner"
          class="bg-red-500 hover:bg-red-300 group"
          @click="handleDeleteGroup"
        >
          <span class="text-white font-bold group-hover:text-black">Supprimer le groupe</span>
        </base-button>
      </template>
      <div v-else>
        <p class="text-center">
          Une erreur s'est produite lors du chargement des données.
        </p>
      </div>
    </div>
    <base-flex-spinner v-else />
  </base-container>
</template>

<script setup lang="ts">
import {computed, inject, ref, watch} from 'vue';
import {HealthcenterGroup, HealthcenterGroupInvitation} from "@/disruption-logistics/interfaces/healthcenter-groups";
import HealthcenterGroupService from "@/disruption-logistics/services/HealthcenterGroupService";
import {disruptionLogisticsApi} from "@/container";
import {useToastStore} from "@/stores/toast.store";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import HeaderBackButton from "@/core/components/header-back-button/HeaderBackButton.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import HealthcenterGroupEditForm
    from "@/disruption-logistics/components/healthcenter-groups/HealthcenterGroupEditForm.vue";
import InviteHealthcenterCard from "@/disruption-logistics/components/healthcenter-groups/InviteHealthcenterCard.vue";
import HealthcenterGroupSentInvitationCard
    from "@/disruption-logistics/components/healthcenter-groups/HealthcenterGroupSentInvitationCard.vue";
import HealthcenterGroupHealthcenters
    from "@/disruption-logistics/components/healthcenter-groups/HealthcenterGroupHealthcenters.vue";
import {useRoute, useRouter} from "vue-router";
import {useSystemStore} from "@/stores/system.store";
import HealthcenterGroupingGroupCard
    from "@/disruption-logistics/components/healthcenter-groups/HealthcenterGroupingGroupCard.vue";

const route = useRoute();
const router = useRouter();
const {environment} = useSystemStore();
const openConfirmationModal = inject('openConfirmationModal') as (
    title: string,
    message: string,
    success: () => never | Promise<void>
) => Promise<void>;
const group = ref<HealthcenterGroup>();
const pendingInvitations = ref<HealthcenterGroupInvitation[]>();
const loading = ref(true);
const editMode = ref(false);

/**
 * Check if the user is the owner of the group.
 */
const isOwner = computed(() => {
    if (!group.value) {
        return false;
    }

    return HealthcenterGroupService.isOwner(group.value.creator_access_id);
});

/**
 * Load the page data.
 */
const load = (groupId: number): Promise<void> => {
    if (groupId === 0 && environment?.entityId) {
        return disruptionLogisticsApi().groupingGroup(environment.entityId)
            .then(response => {
                group.value = response;
            });
    }

    if (groupId !== null) {
        return readGroup(groupId)
            .then(() => {
                if (isOwner.value) {
                    return invitationsSent(groupId);
                }

                return Promise.resolve();
            });
    }

    return Promise.resolve();
};

/**
 * Read a healthcenter group.
 *
 * @param {Number} groupId - The healthcenter group id
 */
const readGroup = (groupId: number) => {
    return disruptionLogisticsApi()
        .readHealthcenterGroup(groupId)
        .then(response => {
            group.value = response;
        });
};

/**
 * Get the invitations sent by the healthcenter group.
 */
const invitationsSent = (groupId: number): Promise<void> => {
    return disruptionLogisticsApi()
        .sentHealthcenterGroupInvitations(groupId, 'pending')
        .then(invitations => {
            pendingInvitations.value = invitations;
        });
};
/**
 * Delete an invitation.
 *
 * @param {Object} invitation - The invitation to delete
 */
const deleteInvitation = (invitation: HealthcenterGroupInvitation) => {
    return disruptionLogisticsApi()
        .deleteHealthcenterGroupInvitation(group.value?.id, invitation.id)
        .then(() => {
            pendingInvitations.value = pendingInvitations.value?.filter(i => i.id !== invitation.id);
        });
};
/**
 * Add the sent invitation to the pending invitations.
 */
const handleInvitationSent = (invitation: HealthcenterGroupInvitation) => {
    const pendingIds = pendingInvitations.value?.map(_invitation => _invitation.id);

    if (!pendingIds?.includes(invitation.id)) {
        pendingInvitations.value?.push(invitation);
    }
};
/**
 * Remove the healthcenter from the group, and update the member count var.
 */
const handleMemberDeleted = (healthcenterId: number) => {
    if (!group.value) {
        return;
    }

    group.value.healthcenters = group.value.healthcenters.filter(healthcenter => healthcenter.id !== healthcenterId);
    group.value.member_count -= 1;
};

/**
 * Ask for confirmation before deleting the group.
 */
const handleDeleteGroup = () => {
    openConfirmationModal(
        'Suppression du groupe',
        'Êtes-vous sûr de vouloir supprimer ce groupe ? Les membres ne pourront plus accéder aux informations du groupe ni aux données partagées.',
        deleteGroup
    );
};

/**
 * Delete the group.
 */
const deleteGroup = (): Promise<void> => {
    if (!group.value) {
        return Promise.resolve();
    }

    return disruptionLogisticsApi()
        .deleteHealthcenterGroup(group.value.id)
        .then(() => {
            const toast = useToastStore();
            toast.showSuccess({content: 'Le groupe a bien été supprimé'});
            router.go(-1);
        });
};

/**
 * Handle the group updated event.
 *
 * @param name
 * @param description
 */
const handleGroupUpdated = ({name, description}: { name: string, description: string }) => {
    if (!group.value) {
        return;
    }

    group.value.name = name;
    group.value.description = description;

    editMode.value = false;
    const toast = useToastStore();
    toast.showSuccess({content: 'Le groupe a bien été mis à jour'});
};

/**
 * Refresh data.
 */
const refresh = () => {
    loading.value = true;
    load(group.value?.id ? group.value.id : parseInt(route.params.healthcenterGroupId as string))
        .finally(() => {
            loading.value = false;
        });
};

watch(() => route.params.healthcenterGroupId, (value) => {
    if (value && typeof value === 'string') {
        loading.value = true;
        load(parseInt(value))
            .finally(() => {
                loading.value = false;
            });
    }
}, {immediate: true});
</script>
